import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { useToast } from "../../../../components/Toast/ToastProvider";
import BackButton from "../../../../components/Button/BackButton";
import ManagerView from "./ManagerView";
import ManagerEditForm from "./ManagerEditForm";

const ManagerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const toast = useToast();

  const isEditing = searchParams.get("edit") === "true";

  const setIsEditing = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set("edit", "true");
    } else {
      params.delete("edit");
    }
    setSearchParams(params, { replace: true });
  };

  const fetchManager = async () => {
    try {
      const response = await api.get(`/managers/${id}`, {
        withCredentials: true,
      });
      setManager(response.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch manager details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManager();
  }, [id]);

  const handleDelete = async () => {
    try {
      await api.delete(`/managers/${id}`, { withCredentials: true });
      navigate("/managers");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete manager");
    }
  };

  const handleResendInvitation = async () => {
    if (resendingInvitation) return;
    setResendingInvitation(true);
    try {
      const response = await api.post(
        `/managers/${id}/resend-invitation`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data?.message || "Activation email has been resent.");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to resend invitation.");
    } finally {
      setResendingInvitation(false);
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return (
      <div className="text-center mt-10 text-red-600 font-medium">{error}</div>
    );

  return (
    <>
      <div className="flex items-center gap-3 mb-6">
        <BackButton
          label={null}
          className="-ml-1.5"
          onClick={isEditing ? () => setIsEditing(false) : undefined}
        />
        <div className="h-5 w-px bg-gray-200" />
        <h1 className="text-xl font-bold text-gray-900">
          {isEditing ? "Update Manager Details" : "Manager Details"}
        </h1>
      </div>

      {!isEditing ? (
        <ManagerView
          manager={manager}
          onEdit={() => setIsEditing(true)}
          onResendInvitation={handleResendInvitation}
          resendingInvitation={resendingInvitation}
          onDelete={handleDelete}
        />
      ) : (
        <ManagerEditForm
          manager={manager}
          id={id}
          onCancel={() => setIsEditing(false)}
          refreshManager={fetchManager}
        />
      )}
    </>
  );
};

export default ManagerDetailPage;
